import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
      if (e.key === 'Tab' && mobileMenuRef.current) {
        const focusable = mobileMenuRef.current.querySelectorAll(
          'button, a, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Close dropdown on Escape or click outside
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && openDropdown) {
        setOpenDropdown(null);
        dropdownRefs.current[openDropdown]?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openDropdown]);

  const navItems = [
    { label: 'Home', path: '/' },
    {
      label: 'About Us',
      dropdown: [
        { label: 'Our Team', path: '/team' },
        { label: 'Careers', path: '/careers' }
      ]
    },
    {
      label: 'Who We Serve',
      dropdown: [
        { label: 'Anesthesiology', path: '/anesthesiology' },
        { label: 'Dermatology', path: '/dermatology' },
        { label: 'Hospitalist', path: '/hospitalist' },
        { label: 'Mental Health', path: '/mental-health' },
        { label: 'OB-GYN', path: '/obgyn' },
        { label: 'Oncology', path: '/oncology' },
        { label: 'Orthopedic', path: '/orthopedic' },
        { label: 'Pathology', path: '/pathology' },
        { label: 'Plastic Surgery', path: '/plastic-surgery' }
      ]
    },
    {
      label: 'Resources',
      dropdown: [
        { label: 'Case Studies', path: '/case-studies' },
        { label: 'Newsletter', path: '/newsletter' },
        { label: 'Blog', path: '/blog' },
        { label: 'FAQs', path: '/faqs' }
      ]
    },
    {
      label: 'Services',
      dropdown: [
        { label: 'All Services', path: '/services' },
        { label: 'Revenue Cycle Management', path: '/revenue-cycle-management' },
        { label: 'Credentialing', path: '/credentialing' },
        { label: 'Medical Billing', path: '/medical-billing' },
        { label: 'Practice Audit', path: '/practice-audit' },
        { label: 'Consulting', path: '/consulting' }
      ]
    },
    { label: 'Contact', path: '/contact' }
  ];

  const handleNavClick = (item) => {
    if (item.path) {
      navigate(item.path);
      setIsMobileMenuOpen(false);
      setOpenDropdown(null);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  const handleDropdownKeyDown = useCallback((e, item) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpenDropdown(prev => prev === item.label ? null : item.label);
    }
    if (e.key === 'ArrowDown' && item.dropdown) {
      e.preventDefault();
      setOpenDropdown(item.label);
      // Focus first dropdown item
      setTimeout(() => {
        const container = dropdownRefs.current[`menu-${item.label}`];
        if (container) {
          const firstItem = container.querySelector('button');
          firstItem?.focus();
        }
      }, 50);
    }
  }, []);

  const handleDropdownItemKeyDown = useCallback((e, item, subItems, idx) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const container = dropdownRefs.current[`menu-${item.label}`];
      if (container) {
        const items = container.querySelectorAll('button');
        const next = items[idx + 1] || items[0];
        next?.focus();
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const container = dropdownRefs.current[`menu-${item.label}`];
      if (container) {
        const items = container.querySelectorAll('button');
        const prev = items[idx - 1] || items[items.length - 1];
        prev?.focus();
      }
    }
    if (e.key === 'Escape') {
      setOpenDropdown(null);
      dropdownRefs.current[item.label]?.focus();
    }
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        isScrolled ? 'shadow-lg' : 'shadow-sm border-b border-gray-100'
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity duration-200 flex items-center"
              aria-label="Capital Billing Services - Go to homepage"
              data-testid="logo-home-btn"
            >
              <img
                src="/assets/images/cbs-logo.png"
                alt="Capital Billing Services logo"
                className="h-16 lg:h-24 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" aria-label="Main navigation">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.dropdown && setOpenDropdown(item.label)}
                onMouseLeave={() => item.dropdown && setOpenDropdown(null)}
              >
                {item.dropdown ? (
                  <>
                    <button
                      ref={(el) => { dropdownRefs.current[item.label] = el; }}
                      className="flex items-center space-x-1 px-3 py-2 text-gray-700 hover:text-teal-600 font-medium transition-colors duration-200 text-sm"
                      aria-expanded={openDropdown === item.label}
                      aria-haspopup="true"
                      aria-controls={`dropdown-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                      onKeyDown={(e) => handleDropdownKeyDown(e, item)}
                      data-testid={`nav-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      <span>{item.label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>
                    <div
                      id={`dropdown-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                      ref={(el) => { dropdownRefs.current[`menu-${item.label}`] = el; }}
                      className={`absolute top-full left-0 mt-0 bg-white shadow-xl rounded-xl py-2 min-w-[220px] z-50 border border-gray-100 transition-all duration-200 ${openDropdown === item.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}
                      role="menu"
                      aria-label={`${item.label} submenu`}
                    >
                      {item.dropdown.map((subItem, idx) => (
                        <button
                          key={subItem.label}
                          onClick={() => handleNavClick(subItem)}
                          onKeyDown={(e) => handleDropdownItemKeyDown(e, item, item.dropdown, idx)}
                          className="block w-full text-left px-4 py-2.5 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors duration-200 text-sm"
                          role="menuitem"
                          data-testid={`nav-${subItem.label.replace(/\s/g, '-').toLowerCase()}`}
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleNavClick(item)}
                    className={`px-3 py-2 font-medium transition-colors duration-200 text-sm ${
                      location.pathname === item.path
                        ? 'text-teal-600'
                        : 'text-gray-700 hover:text-teal-600'
                    }`}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:4709449277"
              className="flex items-center space-x-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-full transition-colors duration-200 text-sm font-medium"
              aria-label="Call Capital Billing Services at 470-944-9277"
              data-testid="header-call-btn"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              <span>Click here to Call</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={menuButtonRef}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-teal-600 transition-colors duration-200"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        ref={mobileMenuRef}
        className={`lg:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-300 ${isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'}`}
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="px-4 py-6 space-y-3 overflow-y-auto max-h-[70vh]">
          {navItems.map((item) => (
            <div key={item.label}>
              {item.dropdown ? (
                <div className="space-y-2">
                  <button
                    onClick={() => setOpenDropdown(prev => prev === item.label ? null : item.label)}
                    className="font-semibold text-gray-900 text-sm w-full text-left flex items-center justify-between py-2"
                    aria-expanded={openDropdown === item.label}
                    aria-controls={`mobile-dropdown-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  <div
                    id={`mobile-dropdown-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                    className={`pl-4 space-y-2 border-l-2 border-teal-200 transition-all duration-200 ${openDropdown === item.label ? 'block' : 'hidden'}`}
                    role="menu"
                  >
                    {item.dropdown.map((subItem) => (
                      <button
                        key={subItem.label}
                        onClick={() => handleNavClick(subItem)}
                        className="block w-full text-left text-gray-600 hover:text-teal-600 py-1.5 transition-colors duration-200 text-sm"
                        role="menuitem"
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => handleNavClick(item)}
                  className="block w-full text-left text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors duration-200 text-sm"
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  {item.label}
                </button>
              )}
            </div>
          ))}
          <a
            href="tel:4709449277"
            className="flex items-center justify-center space-x-2 bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-full transition-colors duration-200 mt-4"
            aria-label="Call Capital Billing Services at 470-944-9277"
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
            <span className="font-medium">Click here to Call</span>
          </a>
        </div>
      </div>
    </header>
  );
};
